import { Navigate, useRoutes } from "react-router-dom";
import Page404 from "./pages/Page404";

import SettingsPage from "./pages/Settings/SettingsPage";
import { useAuthStore } from "./store/useAuthStore";
import LoginPage from "./pages/auth/LoginPage";
import CompanyDashboard from "./pages/user/CompanyPage";
import ClientListPage from "./pages/user/ClientListPage";
import ForgotPasswordPage from "./pages/auth/ForgotPassword";
import ChangePasswordPage from "./pages/auth/ChangePassword";
import SignupPage from "./pages/auth/SignupPage";
import VerifyEmailPage from "./pages/auth/VerifyEmailPage";
import NewLayout from "./pages/layout/NewLayout";
import ReportsPage from "./pages/reports/ReportsPage";
import LocationPage from "./pages/location/LocationPage";
import DevicesPage from "./pages/devices/DevicesPage";
import DeviceDetailPage from "./pages/devices/DeviceDetailPage";
import Dashboard2Page from "./pages/dashboard/DashboardPage";
import AlertsPage from "./pages/alerts/AlertsPage";
import AlertsListPage from "./pages/alerts/AlertsListPage";
import DocumentationPage from "./pages/DocumentationPage";
import CompliancePage from "./pages/compliance/CompliancePage";
import PlatformAdminPage from "./pages/platform/PlatformAdminPage";
import DeactivatedPage from "./pages/auth/DeactivatedPage";
import EmailTemplatePage from "./pages/email/EmailTemplatePage";
import { useIsPlatformAdmin } from "./utils/auth";

function RequireRole({ children, allowed }: { children: React.ReactNode; allowed: string[] }) {
  const { role } = useAuthStore();
  const userRole = role?.toUpperCase() || "";
  if (!allowed.includes(userRole)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

// /user shows the platform console for the allow-listed platform admin
// accounts; everyone else keeps the regular org client list (admin-only).
function UserPageSwitch() {
  const isPlatformAdmin = useIsPlatformAdmin();
  if (isPlatformAdmin) {
    return <PlatformAdminPage />;
  }
  return (
    <RequireRole allowed={["ADMIN", "SUPER_ADMIN"]}>
      <ClientListPage />
    </RequireRole>
  );
}

const RedirectIfAuthenticated = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

export default function Router() {
  const { isAuthenticated } = useAuthStore();
  return useRoutes([
    {
      path: "/login",
      element: <RedirectIfAuthenticated><LoginPage /></RedirectIfAuthenticated>,
    },
    {
      path: "/forgotPassword",
      element: <RedirectIfAuthenticated><ForgotPasswordPage /></RedirectIfAuthenticated>,
    },
    {
      path: "/changePassword",
      element: <RedirectIfAuthenticated><ChangePasswordPage /></RedirectIfAuthenticated>,
    },
    {
      path: "/signup",
      element: <RedirectIfAuthenticated><SignupPage /></RedirectIfAuthenticated>,
    },
    {
      path: "/verify-email",
      element: <RedirectIfAuthenticated><VerifyEmailPage /></RedirectIfAuthenticated>,
    },
    {
      path: "/",
      element: isAuthenticated ? <NewLayout /> : <Navigate to="/login" />,
      children: [
        { index: true, element: <Navigate to="/dashboard" replace /> },
        { path: "settings", element: <SettingsPage /> },
        { path: "dashboard", element: <Dashboard2Page /> },
        { path: "locations", element: <LocationPage /> },
        { path: "devices", element: <DevicesPage /> },
        { path: "devices/:id", element: <DeviceDetailPage /> },
        { path: "compliance", element: <CompliancePage /> },
        { path: "reports", element: <ReportsPage /> },
        { path: "alerts", element: <AlertsPage /> },
        { path: "alerts/manage", element: <AlertsListPage /> },
        { path: "documentation", element: <DocumentationPage /> },
        { path: "email/template", element: <EmailTemplatePage /> },
        {
          path: "user",
          element: <UserPageSwitch />,
        },
        {
          path: "user/:id",
          element: (
            <RequireRole allowed={["ADMIN", "SUPER_ADMIN"]}>
              <CompanyDashboard />
            </RequireRole>
          ),
        },
      ],
    },

    {
      path: "/deactivated",
      element: <DeactivatedPage />,
    },
    {
      path: "/404",
      element: <Page404 />,
    },
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);
}
